import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { translateKnown } from '../i18n/knownMessages';
import { animalLabel } from '../utils/animals';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_STATE, stateLabel } from '../utils/usStates';
import { useGroupStore } from '../hooks/useGroupStore';
import api from '../utils/api';
import { getCurrentUser } from '../utils/auth';

import InfoModal from '../components/common/InfoModal';
import SupplierImages, { imageSrc } from '../components/common/SupplierImages';
import PhotoViewer from '../components/common/PhotoViewer';
import SupplierInfoModal from '../components/common/SupplierInfoModal';
import { NAME_LIMITS } from '../utils/nameLimits';
import TruncatedText from '../components/common/TruncatedText';
import Pagination from '../components/common/Pagination';
import ResultCount from '../components/common/ResultCount';
import { paginate } from '../utils/paginate';
import AdminNav from '../components/admin/AdminNav';

const EPSILON = 0.001;
// True below Tailwind's md breakpoint. Without matchMedia (e.g. jsdom) it is false, so the desktop table is the default
const useIsMobile = () => {
  const query = '(max-width: 767px)';
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [isMobile, setIsMobile] = useState(() => supported && window.matchMedia(query).matches);
  useEffect(() => {
    if (!supported) return undefined;
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [supported]);
  return isMobile;
};
// Pure helpers at module scope so the memoized availability functions below have no component-scope dependencies
const deriveCostPerFull = (group) => group.costPerPortion ? Number(group.costPerPortion) : 0;
const getPortionMultiplier = (p) => (p === 'Half' ? 0.5 : p === 'Quarter' ? 0.25 : 1);

// A group can still be joined only if it is open and has remaining cost left (matches visibleGroups)
const hasAvailablePortions = (group) => {
  const costPerFull = group.costPerPortion ? Number(group.costPerPortion) : 0;
  const rem = Number(group.remainingCost ?? 0);
  return group.status !== 'closed' && costPerFull > 0 && rem / costPerFull > EPSILON;
};

// States the group's supplier delivers to (WA when the supplier has not set any)
const stateOptionsFor = (group) => {
  const allowed = group?.supplierId?.delivery?.allowedStates;
  return allowed?.length ? allowed : [DEFAULT_STATE];
};
const deliveryOffered = (group) => group?.supplierId?.shippingMethods?.delivery !== false;
const defaultStateFor = (group) => {
  const options = stateOptionsFor(group);
  return options.includes(DEFAULT_STATE) ? DEFAULT_STATE : options[0];
};

const EMPTY_ADDRESS ={ name: '', street: '', city: '', state: DEFAULT_STATE, zip: '', phone: '' };
const ADDRESS_INPUTS = [
  ['name', 'Full name'],
  ['street', 'Street address'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP code'],
  ['phone', 'Phone number'],
];

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function PortionSelector({ group, portion, onChange, isOptionDisabled }) {
  const { t } = useTranslation();
  const options = group.portionsAvailable || ['Full', 'Half', 'Quarter'];

  return (
    <select value={portion} onChange={(e) => onChange(e.target.value)} className="border p-2 rounded w-full mb-4">
      <option value="">{t('groups.choosePortion')}</option>
      {options.map((opt) => {
        const disabled = isOptionDisabled ? isOptionDisabled(opt) : false;
        return (
          <option key={opt} value={opt} disabled={disabled}>
            {t('portions.' + opt.toLowerCase())} {disabled ? t('portions.unavailable') : ''}
          </option>
        );
      })}
    </select>
  );
}

// Two-line clamped plain-text description; the "More" button appears only when the text overflows
function ClampedDescription({ text, moreLabel, onMore }) {
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text]);
  return (
    <>
      <p ref={ref} className="mt-1 text-sm text-gray-600 break-words line-clamp-2">{text}</p>
      {overflows && (
        <button type="button" onClick={onMore} className="mt-1 block text-xs text-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">{moreLabel}</button>
      )}
    </>
  );
}

export default function SearchAndJoinGroup() {
  const { t } = useTranslation();
  const { groups: searchResults, fetchGroups, fetchPublicGroups } = useGroupStore();
  const isMobile = useIsMobile();
  const selectedPanelRef = useRef(null);
  const [mobilePhoto, setMobilePhoto] = useState(null); // { photos, index } for the phone cover photo viewer
  const scrollToPanelRef = useRef(false); // set only by a phone card's Select, so the scroll never fires on other updates
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoaded, setSuppliersLoaded] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [portion, setPortion] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [fulfillment, setFulfillment] = useState('pickup'); // 'pickup' | 'delivery'
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [pickupPhone, setPickupPhone] = useState('');
  const [cartItems, setCartItems] = useState([]); // this user's cart, used to show portions left after cart quantities
  const [favoriteIds, setFavoriteIds] = useState([]); // group ids this user saved as favorites (from the server)
  const [alertIds, setAlertIds] = useState([]); // saved groups with availability alerts on (default off)
  const [favoriteBusy, setFavoriteBusy] = useState([]); // group ids with a save/unsave request in flight
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteNotice, setFavoriteNotice] = useState('');
  const [guestToast, setGuestToast] = useState(false); // guest sign-in prompt for Favorites actions (auto-dismisses)
  const guestToastTimer = useRef(null);
  const showGuestToast = () => {
    clearTimeout(guestToastTimer.current); // repeated taps refresh the same toast
    setGuestToast(true);
    guestToastTimer.current = setTimeout(() => setGuestToast(false), 5000);
  };
  useEffect(() => () => clearTimeout(guestToastTimer.current), []);
  const [pendingIds, setPendingIds] = useState([]); // group ids with this user's pending join request (from the server)
  const [groupInfo, setGroupInfo] = useState(null); // group whose details modal is open
  const [deliveryQuote, setDeliveryQuote] = useState(null); // { deliveryFee, estimatedDays } for the current address
  const [fadingGroupId] = useState(null);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [modalInfo, setModalInfo] = useState({ isOpen: false, title: '', content: null });
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierForModal, setSupplierForModal] = useState(null);
  const [supplierModalGroup, setSupplierModalGroup] = useState(null); // group the supplier modal's Ask to Join button targets
  const [reserveGroup, setReserveGroup] = useState(null); // group a non-member tried to reserve in
  const [joinRequest, setJoinRequest] = useState({ groupId: null, status: 'idle', message: '' });

  // Memoized: a new object each render would re-run the supplier effect and reset the dropdown
  const currentUser = useMemo(() => getCurrentUser(), []);
  const query = useQuery();
  const location = useLocation();
  // The URL groupId is a deep link: it narrows results until the user changes a filter or selection.
  // It is re-read on every navigation (location.key), so a second notification link works on the open page.
  const [deepLinkId, setDeepLinkId] = useState(() => query.get('groupId'));
  useEffect(() => { setDeepLinkId(query.get('groupId')); }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // A deep link to a saved group turns on the Favorites view so it stays visible if it became Full/Closed
  useEffect(() => {
    if (deepLinkId && favoriteIds.includes(String(deepLinkId))) setFavoritesOnly(true);
  }, [deepLinkId, favoriteIds]);

  // Scoped user: supplieradmin or member. Users linked to several suppliers get the supplier dropdown instead.
  const linkedSupplierIds = currentUser?.supplierIds || [];
  const scopedUser =
    currentUser &&
    ['supplieradmin', 'member'].includes((currentUser.role || '').toLowerCase()) &&
    linkedSupplierIds.length === 1;
  // Member not linked to any supplier yet: browse public groups and request to join
  const unlinkedMember = currentUser?.role === 'member' && linkedSupplierIds.length === 0;
  // Visitors and unlinked members browse public groups; the supplier filter is optional for them
  const publicBrowsing = !currentUser || unlinkedMember;

  // Suppliers offered in the public filter, taken from the public groups already loaded
  const publicSuppliers = useMemo(() => {
    if (!publicBrowsing) return [];
    const byId = new Map();
    (Array.isArray(searchResults) ? searchResults : []).forEach((g) => {
      // Only suppliers with a public group that still has portions to join
      if (g.supplierId?._id && hasAvailablePortions(g)) byId.set(g.supplierId._id, g.supplierId);
    });
    return [...byId.values()];
  }, [publicBrowsing, searchResults]);
  const supplierOptions = publicBrowsing ? publicSuppliers : suppliers;

  const showModal = (title, content) => setModalInfo({ isOpen: true, title, content });
  const closeModal = () => setModalInfo({ ...modalInfo, isOpen: false });

  // Loads whether the logged-in user already has a pending request, so the button can show "Request Pending"
  const checkJoinRequest = async (group) => {
    setJoinRequest({ groupId: group._id, status: 'checking', message: '' });
    try {
      const res = await api.get(`/api/groups/${group._id}/join-requests/mine`);
      setJoinRequest(res.data?.pending
        ? { groupId: group._id, status: 'pending', message: 'Your request is awaiting review by the supplier.' }
        : { groupId: group._id, status: 'idle', message: '' });
    } catch (err) {
      setJoinRequest({ groupId: group._id, status: 'error', message: 'Could not check your request status. Please try again.' });
    }
  };

  const openSupplierModal = (supplier, group = null) => {
    if (!supplier) return;
    setSupplierForModal(supplier);
    setSupplierModalGroup(group);
    setSupplierModalOpen(true);
    // Only non-members of the group can ask to join; checking shows an existing pending request
    if (currentUser && group && !isJoined(group)) checkJoinRequest(group);
  };

  // Sends a join request. Success is shown only when the backend confirms it.
  const handleRequestJoin = async (group) => {
    setJoinRequest({ groupId: group._id, status: 'sending', message: '' });
    try {
      const res = await api.post(`/api/groups/${group._id}/join-requests`);
      setJoinRequest({ groupId: group._id, status: 'sent', message: res.data?.message || 'Your request was sent to the supplier.' });
      setPendingIds((prev) => [...prev, String(group._id)]);
    } catch (err) {
      const isPending = err.response?.status === 409;
      if (isPending) setPendingIds((prev) => [...prev, String(group._id)]);
      setJoinRequest({
        groupId: group._id,
        status: isPending ? 'pending' : 'error',
        message: err.response?.data?.message || 'Could not send your request. Please try again.',
      });
    }
  };
  const closeSupplierModal = () => {
    setSupplierModalOpen(false);
    setSupplierForModal(null);
    setSupplierModalGroup(null);
  };

  // Initialize suppliers
  useEffect(() => {
    const initSuppliers = async () => {
      try {
        const role = (currentUser?.role || '').toLowerCase();
        const isScopedSupplierAdmin = ['supplieradmin', 'member'].includes(role) || (role === 'admin' && currentUser?.supplierId);

        // Keep only suppliers that have at least one group with remaining portions
        const withAvailablePortions = async (suppliersList) => {
          const res = await api.get('/api/groups/search');
          const groups = Array.isArray(res.data) ? res.data : res.data.groups || [];
          // A deep-linked group keeps its supplier even when it is Full/Closed, so its link still loads
          const qGroupId = query.get('groupId');
          const supplierIdsWithPortions = new Set(
            groups.filter((g) => hasAvailablePortions(g) || (qGroupId && String(g._id) === qGroupId)).map((g) => g.supplierId?._id || g.supplierId)
          );
          return suppliersList.filter((s) => supplierIdsWithPortions.has(s._id));
        };

        if (unlinkedMember) {
          // No supplier to scope to, so the groups list comes from the public endpoint
          setSuppliers([]);
          setSupplierId('');
          await fetchPublicGroups();
        } else if (isScopedSupplierAdmin) {
          const res = await api.get('/api/suppliers/my-suppliers');
          const list = await withAvailablePortions(Array.isArray(res.data) ? res.data : []);
          if (list.length > 0) {
            setSuppliers(list);
            const qSupplierId = query.get('supplierId');
            setSupplierId(qSupplierId && list.find((s) => s._id === qSupplierId) ? qSupplierId : '');
          } else {
            setSuppliers([]);
            setSupplierId('');
          }
        } else if (role === 'superadmin') {
          const res = await api.get('/api/suppliers');
          const list = await withAvailablePortions(Array.isArray(res.data) ? res.data : res.data.suppliers || []);
          setSuppliers(list);
          const qSupplierId = query.get('supplierId');
          if (qSupplierId && list.find((s) => s._id === qSupplierId)) setSupplierId(qSupplierId);
        } else {
          setSuppliers([]);
          setSupplierId('');
        }
        setSuppliersLoaded(true);
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        showModal('Error', 'Failed to fetch suppliers. Please try again later.');
      }
    };
    if (currentUser) initSuppliers();
  }, [currentUser, query, unlinkedMember, fetchPublicGroups]);

  // Visitors (not logged in) can browse public groups; they cannot reserve shares
  useEffect(() => {
    if (currentUser) return;
    setSearching(true);
    setSearchError(null);
    fetchPublicGroups()
      .catch((err) => {
        console.error('Public groups error:', err);
        setSearchError('Failed to fetch groups');
      })
      .finally(() => setSearching(false));
  }, [currentUser, fetchPublicGroups]);

  // Availability comes from the server again when the cart changes or the user returns to this page
  useEffect(() => {
    const refresh = () => {
      if (publicBrowsing) fetchPublicGroups().catch(() => {});
      else if (suppliers.length) fetchGroups(supplierId ? { supplierId } : {}).catch(() => {});
    };
    window.addEventListener('cart-changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('cart-changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [publicBrowsing, supplierId, suppliers.length, fetchGroups, fetchPublicGroups]);

  // Keep the cart in step with the page so availability reflects what is already in the cart
  useEffect(() => {
    if (!currentUser) return undefined;
    const load = () => api.get('/api/cart').then((res) => setCartItems(res.data?.items || [])).catch(() => {});
    load();
    window.addEventListener('cart-changed', load);
    return () => window.removeEventListener('cart-changed', load);
  }, [currentUser]);

  // Load the user's pending join requests from the server so Request Pending survives refresh
  useEffect(() => {
    if (!currentUser) return;
    api.get('/api/groups/my-join-requests')
      .then((res) => setPendingIds(res.data?.groupIds || []))
      .catch(() => {});
  }, [currentUser]);

  // Load the user's saved favorites so the hearts and the Favorites filter survive refresh
  useEffect(() => {
    if (!currentUser) return;
    api.get('/api/groups/my-favorites')
      .then((res) => { setFavoriteIds(res.data?.groupIds || []); setAlertIds(res.data?.alertGroupIds || []); })
      .catch(() => {});
  }, [currentUser]);

  // Fetch groups
  useEffect(() => {
    // Public browsing filters the groups already loaded, so it never calls the members-only search
    // An empty supplierId means All Suppliers; skip only while no suppliers are available
    if (publicBrowsing || suppliers.length === 0) return;
    const qGroupId = query.get('groupId');
    const params = supplierId ? { supplierId } : {};
    if (qGroupId) params.groupId = qGroupId;

    const doFetch = async () => {
      setSearching(true);
      setSearchError(null);
      try {
        await fetchGroups(params);
        setPortion('');
        setCalculatedPrice(0);
      } catch (err) {
        console.error('Search error:', err);
        setSearchError('Failed to fetch groups');
        showModal('Error', 'Failed to fetch groups. Please try again later.');
      } finally {
        setSearching(false);
      }
    };
    doFetch();
  }, [supplierId, suppliers.length, fetchGroups, query, publicBrowsing]);

  // Scroll to the group named in the URL once the results have loaded
  useEffect(() => {
    const qGroupId = deepLinkId;
    if (!qGroupId || !searchResults.some((g) => g._id === qGroupId)) return;
    document.getElementById(`group-row-${qGroupId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [searchResults, deepLinkId]);

  // Utility
  // Full-portion units this user already has in the cart for a group
  const cartUnitsFor = useCallback((groupId) => cartItems
    .filter((i) => String(i.groupId) === String(groupId))
    .reduce((sum, i) => sum + i.quantity * getPortionMultiplier(i.portion), 0), [cartItems]);
  // Actual stock left (cart holds do not change it): drives visibility, Full status and selecting a group
  const rawPortionsRemaining = (group) => {
    const costPerFull = deriveCostPerFull(group);
    return costPerFull ? Number(group.remainingCost ?? 0) / costPerFull : 0;
  };
  const computePrecisePortionsRemaining = useCallback((group, portionType = 'Full') => {
    const costPerFull = deriveCostPerFull(group);
    // Cost already in the cart is not available to others, so it is taken off the remaining cost
    const rem = Number(group.remainingCost ?? 0) - cartUnitsFor(group._id) * costPerFull;
    if (!costPerFull) return 0;
    return rem / (costPerFull * getPortionMultiplier(portionType));
  }, [cartUnitsFor]);
  const formatPortionFraction = (value) => {
    if (value <= 0) return '0';
    const whole = Math.floor(value);
    const fraction = value - whole;
    let fracLabel = '';
    if (fraction >= 0.75) fracLabel = '3/4';
    else if (fraction >= 0.5) fracLabel = '1/2';
    else if (fraction >= 0.25) fracLabel = '1/4';
    if (whole === 0 && fracLabel) return fracLabel;
    if (whole > 0 && fracLabel) return `${whole} ${fracLabel}`;
    return `${whole}`;
  };

  // Smallest portion (Quarter) no longer fits: the purchase form is replaced by a View Cart notice
  const noPortionLeft = !!selectedGroup && computePrecisePortionsRemaining(selectedGroup) + EPSILON < getPortionMultiplier('Quarter');

  // A portion the group can no longer cover (e.g. after a cart change) is cleared so no stale choice stays selected
  useEffect(() => {
    if (selectedGroup && portion && computePrecisePortionsRemaining(selectedGroup) + EPSILON < getPortionMultiplier(portion)) {
      setPortion('');
      setCalculatedPrice(0);
    }
  }, [selectedGroup, portion, computePrecisePortionsRemaining]);

  // The selected group is a snapshot: take the refreshed copy so availability after a cart change or another buyer shows
  useEffect(() => {
    if (!selectedGroup || !Array.isArray(searchResults)) return;
    const fresh = searchResults.find((g) => g._id === selectedGroup._id);
    if (fresh && fresh !== selectedGroup) setSelectedGroup(fresh);
  }, [searchResults, selectedGroup]);

  // Handlers
  const handleSearch = async () => {
    setDeepLinkId(null);
    setSearching(true);
    setSearchError(null);
    try {
      const params = {};
      if (supplierId) params.supplierId = supplierId;
      // Group name is filtered below; /api/groups/search does not read groupName
      if (publicBrowsing) await fetchPublicGroups();
      else await fetchGroups(params);
      clearSelection();
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Failed to search groups');
      showModal('Error', 'Failed to search groups. Please try again later.');
    } finally {
      setSearching(false);
    }
  };
  const resetCheckout = (group) => {
    setFulfillment('pickup');
    setAddress({ ...EMPTY_ADDRESS, state: defaultStateFor(group) });
    setDeliveryQuote(null);
  };
  const clearSelection = () => {
    setDeepLinkId(null);
    setSelectedGroup(null);
    setPortion('');
    setCalculatedPrice(0);
    resetCheckout();
  };
  const handleSelectGroup = (group) => {
    setDeepLinkId(null);
    // Only logged-in members can reserve; the server enforces this too
    if (!isJoined(group)) {
      setReserveGroup(group);
      if (currentUser) checkJoinRequest(group);
      return;
    }
    if (rawPortionsRemaining(group) <= EPSILON) return;
    setSelectedGroup(group);
    setPortion('');
    setCalculatedPrice(0);
    resetCheckout(group);
  };
  const handlePortionChange = (portionValue) => {
    setPortion(portionValue);
    if (!selectedGroup) return;
    setCalculatedPrice(deriveCostPerFull(selectedGroup) * getPortionMultiplier(portionValue));
  };
  const handleAddressChange = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
    setDeliveryQuote(null);
  };
  const deliveryFee = fulfillment === 'delivery' && deliveryQuote ? Number(deliveryQuote.deliveryFee || 0) : 0;
  const cuttingPackagingFee = Number(selectedGroup?.supplierId?.cuttingPackagingFee || 0);
  const grandTotal = calculatedPrice + deliveryFee + cuttingPackagingFee;

  // Adds one portion of the selected group to the cart (paid together at /cart)
  const addToCart = async () => {
    // Pickup needs a phone number before the item goes into the cart (same check as Proceed to Pay)
    if (fulfillment === 'pickup' && !/^\+?[\d\s()-]{7,20}$/.test(pickupPhone.trim())) {
      return showModal('Phone Required', 'Please enter a phone number so the supplier can reach you about pickup');
    }
    // Refuse before sending when the portions left (after the cart) cannot cover this choice
    if (computePrecisePortionsRemaining(selectedGroup) + EPSILON < getPortionMultiplier(portion)) {
      return showModal('Not enough portions', 'There are not enough portions left in this group for that choice.');
    }
    if (fulfillment === 'delivery' && !deliveryOffered(selectedGroup)) {
      return showModal('Error', 'This supplier does not offer delivery. Choose Pickup.');
    }
    try {
      await api.post('/api/cart/items', {
        groupId: selectedGroup._id, portion, quantity: 1,
        fulfillment,
        deliveryAddress: fulfillment === 'delivery' ? address : undefined,
        contactPhone: fulfillment === 'pickup' ? pickupPhone.trim() : undefined,
      });
      window.dispatchEvent(new Event('cart-changed'));
      showModal('Cart', 'Added to your cart.');
    } catch (err) {
      showModal('Error', err.response?.data?.message || 'Could not add to cart.');
    }
  };
  // Groups the logged-in user already belongs to (members come populated with _id)
  const isJoined = (group) =>
    !!currentUser?.userId &&
    (group.members || []).some((m) => String(m?._id ?? m) === String(currentUser.userId));

  // Case-insensitive name match on the fetched groups; an empty search keeps the full list
  const nameFilter = groupName.trim().toLowerCase();
  const linkedGroupId = deepLinkId ? String(deepLinkId) : null;
  // The current user's membership, shown separately from group visibility (Public/Private)
  const membershipOf = (g) => {
    if (isJoined(g)) return 'Joined';
    const awaiting = pendingIds.includes(String(g._id)) || (joinRequest.groupId === g._id && (joinRequest.status === 'pending' || joinRequest.status === 'sent'));
    return awaiting ? 'Request Pending' : 'Not Joined';
  };
  const membershipText = (value) => t('groups.membership.' + ({ Joined: 'joined', 'Request Pending': 'requestPending' }[value] || 'notJoined'));
  const visibleGroups = Array.isArray(searchResults)
    ? searchResults
      .filter(g => !nameFilter || (g.name || '').toLowerCase().includes(nameFilter))
      // A link to one group shows only that group, matched by database _id (display only, nothing is selected)
      .filter(g => !linkedGroupId || String(g._id) === linkedGroupId)
      // Favorites view: only saved groups that are still in the fetched results (removed groups drop out)
      .filter(g => !favoritesOnly || favoriteIds.includes(String(g._id)))
      // Favorites keep saved full and closed groups visible (shown as unavailable); regular search still hides them
      .filter(g => favoritesOnly || (g.status !== 'closed' && rawPortionsRemaining(g) > EPSILON))
      .filter(g => {
        // Public groups: all suppliers unless one is picked
        if (publicBrowsing) return !supplierId || g.supplierId?._id === supplierId;
        if (currentUser?.role === 'member' || currentUser?.role === 'supplieradmin') {
          return linkedSupplierIds.includes(g.supplierId?._id);
        }
        return true;
      })
    : [];

  // Underlying value per sortable column, so sorting ignores formatted display text
  const SORT_VALUES = {
    group: (g) => (g.name || '').toLowerCase(),
    status: (g) => (isJoined(g) ? 'joined' : ''),
    supplier: (g) => (g.supplierId?.name || '').toLowerCase(),
    animal: (g) => (g.animal || '').toLowerCase(),
    cost: (g) => deriveCostPerFull(g),
    portions: (g) => computePrecisePortionsRemaining(g, 'Full'),
  };
  const sortedGroups = useMemo(() => {
    if (!sortConfig.key) return visibleGroups;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const valueOf = SORT_VALUES[sortConfig.key];
    return [...visibleGroups].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    // SORT_VALUES is rebuilt each render; the sort only depends on the inputs below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleGroups, sortConfig]);

  const sortByKey = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  const sortArrow = (key) => <span className="inline-block w-3 text-center">{sortConfig.key !== key ? '' : sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

  // Phone cards for the same paged groups: same handlers and calculations as the table row
  // After a phone card's Select, bring the order form into view once it has rendered (no scroll when reduced motion is requested)
  useEffect(() => {
    if (!scrollToPanelRef.current || !selectedGroup) return;
    scrollToPanelRef.current = false;
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    selectedPanelRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [selectedGroup]);
  // Escape closes the phone photo viewer
  useEffect(() => {
    if (!mobilePhoto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobilePhoto(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobilePhoto]);
  const selectFromCard = (group) => {
    scrollToPanelRef.current = true;
    handleSelectGroup(group);
  };

  // Save or unsave a group. Guests are asked to sign in; duplicate taps are ignored while a request is in flight.
  const toggleFavorite = async (group) => {
    if (!currentUser) { showGuestToast(); return; }
    const id = String(group._id);
    if (favoriteBusy.includes(id)) return;
    const wasSaved = favoriteIds.includes(id);
    setFavoriteNotice('');
    setFavoriteBusy((prev) => [...prev, id]);
    setFavoriteIds((prev) => (wasSaved ? prev.filter((x) => x !== id) : [...prev, id]));
    // Removing a favorite also turns off its availability alerts (the server does the same)
    if (wasSaved) setAlertIds((prev) => prev.filter((x) => x !== id));
    try {
      if (wasSaved) await api.delete(`/api/groups/${id}/favorite`);
      else await api.post(`/api/groups/${id}/favorite`);
    } catch {
      setFavoriteIds((prev) => (wasSaved ? [...prev, id] : prev.filter((x) => x !== id)));
      setFavoriteNotice(t('groups.favorites.failed'));
    } finally {
      setFavoriteBusy((prev) => prev.filter((x) => x !== id));
    }
  };
  const renderFavoriteButton = (group) => {
    const id = String(group._id);
    const saved = favoriteIds.includes(id);
    const label = t(saved ? 'groups.favorites.remove' : 'groups.favorites.add');
    return (
      <button type="button" onClick={() => toggleFavorite(group)} disabled={favoriteBusy.includes(id)} aria-pressed={saved} aria-label={label} title={label} className="shrink-0 rounded p-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50">
        <svg viewBox="0 0 24 24" aria-hidden="true" className={`h-5 w-5 ${saved ? 'text-red-500' : 'text-gray-400'}`} fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>
    );
  };

  // Unavailable = closed, or no full portion left (same rule as the Full/Closed buttons)
  const isUnavailableGroup = (group) => group.status === 'closed' || rawPortionsRemaining(group) <= EPSILON;
  // Turn availability alerts on or off for a saved group. Enabling never alerts immediately.
  const toggleAlert = async (group) => {
    const id = String(group._id);
    if (!currentUser || favoriteBusy.includes(id)) return;
    const wasOn = alertIds.includes(id);
    setFavoriteNotice('');
    setFavoriteBusy((prev) => [...prev, id]);
    setAlertIds((prev) => (wasOn ? prev.filter((x) => x !== id) : [...prev, id]));
    try {
      if (wasOn) await api.delete(`/api/groups/${id}/alerts`);
      else await api.put(`/api/groups/${id}/alerts`);
    } catch {
      setAlertIds((prev) => (wasOn ? [...prev, id] : prev.filter((x) => x !== id)));
      setFavoriteNotice(t('notify.alertFailed'));
    } finally {
      setFavoriteBusy((prev) => prev.filter((x) => x !== id));
    }
  };
  // Bell shown only for a saved group that is unavailable right now
  const renderAlertButton = (group) => {
    const id = String(group._id);
    if (!currentUser || !favoriteIds.includes(id) || !isUnavailableGroup(group)) return null;
    const on = alertIds.includes(id);
    const label = t(on ? 'notify.disable' : 'notify.enable');
    return (
      <button type="button" onClick={() => toggleAlert(group)} disabled={favoriteBusy.includes(id)} aria-pressed={on} aria-label={label} title={label} className={`shrink-0 rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 ${on ? 'bg-amber-50 text-amber-600' : 'text-gray-400 hover:bg-gray-100'}`}>
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
      </button>
    );
  };

  const renderMobileCards = () => (
    <div className="mt-4 space-y-3">
      {pagedGroups.map(group => {
        const fullPortionsLeft = rawPortionsRemaining(group);
        const isFull = fullPortionsLeft <= EPSILON;
        const joined = isJoined(group);
        const selected = selectedGroup?._id === group._id;
        const photos = [group.images?.primary, ...(group.images?.gallery || [])].filter(Boolean);
        const supplier = group.supplierId;
        return (
          <div key={group._id} id={`group-card-${group._id}`} className={`border rounded-lg shadow-sm p-4 min-w-0 transition duration-200 hover:shadow-md motion-safe:hover:-translate-y-0.5 motion-safe:active:scale-[0.99] ${isFull ? 'bg-gray-100 text-gray-500' : joined ? 'bg-green-50 border-l-4 border-green-500' : 'bg-white'}`}>
            <div className="flex items-start gap-3">
              {photos[0] && (
                <button type="button" onClick={() => setMobilePhoto({ photos: photos.slice(0, 5), index: 0 })} className="relative shrink-0 self-start" aria-label="View group photos">
                  <img src={imageSrc(photos[0])} alt="" className="h-20 w-20 object-cover rounded border" />
                  {photos.length > 1 && <span className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1 rounded">{photos.length} photos</span>}
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2"><div className="truncate min-w-0 flex-1 font-semibold text-gray-800" title={group.name}>{group.name}</div>{renderFavoriteButton(group)}{renderAlertButton(group)}</div>
                <div className="text-sm text-gray-600 break-words">{animalLabel(t, group.animal)}</div>
                {group.shortDescription && (
                  <ClampedDescription text={group.shortDescription} moreLabel={t('groupDetails.more')} onMore={() => setGroupInfo(group)} />
                )}
              </div>
            </div>
            <span className={`block w-fit mt-2 px-2 py-1 rounded text-xs font-medium text-white ${membershipOf(group) === 'Joined' ? 'bg-green-600' : membershipOf(group) === 'Request Pending' ? 'bg-amber-500' : 'bg-gray-500'}`}>{membershipText(membershipOf(group))}</span>
            <div className="mt-2 text-sm space-y-1 break-words">
              <div>{t('groups.card.supplier')} {supplier?.name ? (
                <button type="button" onClick={() => openSupplierModal(supplier, group)} className="inline-block max-w-full truncate align-bottom text-blue-600 hover:underline" title={supplier.name}>{supplier.displayName || supplier.name}</button>
              ) : 'N/A'}</div>
              <div>{t('groups.card.rating')} {supplier?.ratingCount ? `${Number(supplier.ratingAverage).toFixed(1)} ★ (${supplier.ratingCount})` : t('groups.card.noReviews')}</div>
              <div>{t('groups.card.pricePerFull')} ${deriveCostPerFull(group).toFixed(2)}</div>
              <div>{t('groups.card.portionsRemainingFull')} {formatPortionFraction(fullPortionsLeft)}</div>
            </div>
            <div className="mt-3">
              {isFull || group.status === 'closed' ? (
                <button disabled className="w-full px-2 py-2 rounded bg-gray-300 text-white cursor-not-allowed">{group.status === 'closed' ? t('groups.closed') : t('groups.full')}</button>
              ) : (
                <button type="button" onClick={() => (selected ? clearSelection() : selectFromCard(group))} className={`w-full px-2 py-2 rounded text-white ${selected ? 'bg-red-600' : 'bg-blue-600'}`}>{selected ? t('groups.unselect') : t('groups.select')}</button>
              )}
            </div>
          </div>
        );
      })}
      {mobilePhoto && (
        <PhotoViewer photos={mobilePhoto.photos} index={mobilePhoto.index} onIndex={(i) => setMobilePhoto({ ...mobilePhoto, index: i })} onClose={() => setMobilePhoto(null)} />
      )}
    </div>
  );

  const { pageItems: pagedGroups, page: currentPage, pageCount } = paginate(sortedGroups, page);

  return (
    <>
    <AdminNav />
    <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-gray-800 min-w-0">{t('groups.title')}</h1>
        {currentUser && (
          <button type="button" onClick={() => { setDeepLinkId(null); setFavoritesOnly((v) => !v); setPage(1); }} aria-pressed={favoritesOnly} className={`ml-auto inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm whitespace-normal text-left ${favoritesOnly ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0" fill={favoritesOnly ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            {t('groups.favorites.filter')}
          </button>
        )}
      </div>

      <div className="space-y-2 mb-4">
        <label className="font-medium">{t('groups.selectSupplier')}</label>
        {scopedUser ? (
          <div className="border p-2 rounded w-full bg-gray-100 flex justify-between items-center">
            <span className="block min-w-0"><TruncatedText text={suppliers.find(s => s._id === currentUser.supplierId)?.name || 'No associated supplier'} maxWidthClass="max-w-[20rem]" /></span>
            <input type="hidden" name="supplierId" value={currentUser.supplierId} />
          </div>
        ) : (
          <>
            <select value={supplierId} onChange={(e) => { setDeepLinkId(null); setSupplierId(e.target.value); setPage(1); }} className="border p-2 w-full" disabled={!publicBrowsing && suppliers.length < 1}>
              <option value="">{t('groups.allSuppliers')}</option>
              {supplierOptions.map(s => <option key={s._id} value={s._id}>{s.displayName || s.name}</option>)}
            </select>
            {unlinkedMember && <p className="text-gray-600 mt-1">{t('groups.notLinked')}</p>}
            {!publicBrowsing && suppliersLoaded && suppliers.length < 1 && <p className="text-red-600 mt-1">{t('groups.noSuppliers')}</p>}
          </>
        )}
        <input type="text" value={groupName} onChange={e => { setDeepLinkId(null); setGroupName(e.target.value); setPage(1); }} maxLength={NAME_LIMITS.groupName.max} placeholder={t('groups.groupNamePlaceholder')} className="border p-2 w-full" />
        <button onClick={() => { setPage(1); handleSearch(); }} className="bg-blue-600 text-white px-4 py-2 rounded" disabled={searching || (!publicBrowsing && suppliers.length < 1)}>{searching ? t('groups.searching') : t('groups.search')}</button>
        {searchError && <p className="text-red-500">{translateKnown(t, searchError)}</p>}
        {favoriteNotice && <p role="status" className="text-sm mt-1 text-red-600">{favoriteNotice}</p>}
        {guestToast && (
          <div role="status" className="fixed left-3 right-3 mx-auto max-w-sm z-50 flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg" style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
            <span className="flex-1">{t('groups.favorites.signInRequired')}</span>
            <Link to="/login" onClick={() => setGuestToast(false)} className="rounded bg-white px-2 py-1 font-semibold text-gray-900 hover:bg-gray-100">{t('groups.favorites.signIn')}</Link>
            <button type="button" onClick={() => setGuestToast(false)} aria-label={t('notify.close')} className="rounded p-1 text-gray-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        )}
      </div>

      <ResultCount total={sortedGroups.length} page={currentPage} />
      {visibleGroups.length > 0 ? (isMobile ? renderMobileCards() : (
        <div className="overflow-x-auto mt-4"> {/* scrolls wide tables instead of stretching the page and moving the nav */}
        <table className="w-full min-w-[60rem] table-fixed bg-white border rounded shadow">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-3 text-left w-56 cursor-pointer" onClick={() => sortByKey('group')}>{t('groups.cols.group')}{sortArrow('group')}</th>
              <th className="p-3 text-left w-28 cursor-pointer" onClick={() => sortByKey('status')}>{t('groups.cols.status')}{sortArrow('status')}</th>
              <th className="p-3 text-left w-48 cursor-pointer" onClick={() => sortByKey('supplier')}>{t('groups.cols.supplier')}{sortArrow('supplier')}</th>
              <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('animal')}>{t('groups.cols.animal')}{sortArrow('animal')}</th>
              <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('cost')}>{t('groups.cols.costPerFull')}{sortArrow('cost')}</th>
              <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('portions')}>{t('groups.cols.portionsRemaining')}{sortArrow('portions')}</th>
              <th className="p-3 text-left">{t('groups.cols.action')}</th>
            </tr>
          </thead>
          <tbody>
            {pagedGroups.map(group => {
              const fullPortionsLeft = rawPortionsRemaining(group);
              const isFull = fullPortionsLeft <= EPSILON;
              const fadeClass = group._id === fadingGroupId ? 'opacity-0 transition-opacity duration-700' : 'opacity-100';
              const joined = isJoined(group);
              const rowClass = isFull
                ? 'bg-gray-100 text-gray-500'
                : joined
                ? 'bg-green-50 border-l-4 border-green-500'
                : 'bg-white';
              const costPerFull = deriveCostPerFull(group);

              return (
                <tr key={group._id} id={`group-row-${group._id}`} className={`text-left align-top ${fadeClass} ${rowClass}`}>
                  <td className="p-3 text-left">
                    <div className="flex flex-nowrap items-center gap-1 min-w-0">
                      <div className="flex shrink-0 items-center">
                        {renderFavoriteButton(group)}
                        {renderAlertButton(group)}
                      </div>
                      <button type="button" onClick={() => setGroupInfo(group)} className="min-w-0 text-left text-blue-600 hover:underline focus:outline-none">
                        <TruncatedText text={group.name} maxWidthClass="max-w-[12rem]" className={joined ? 'font-semibold' : ''} />
                      </button>
                    </div>
                  </td>
                  <td className="p-3 text-left">
                    <span className={`inline-block max-w-full break-words px-2 py-1 rounded text-xs font-medium text-white ${membershipOf(group) === 'Joined' ? 'bg-green-600' : membershipOf(group) === 'Request Pending' ? 'bg-amber-500' : 'bg-gray-500'}`}>
                      {membershipText(membershipOf(group))}
                    </span>
                  </td>
                  <td className="p-3">
                    {group.supplierId?.name ? (
                      <button type="button" onClick={() => openSupplierModal(group.supplierId, group)} className="text-blue-600 hover:underline focus:outline-none" title="View Supplier Details"><TruncatedText text={group.supplierId.displayName || group.supplierId.name} maxWidthClass="max-w-[10rem]" /></button>
                    ) : 'N/A'}
                  </td>
                  <td className="p-3">{animalLabel(t, group.animal)}</td>
                  <td className="p-3">${costPerFull.toFixed(2)}</td>
                  <td className="p-3">{formatPortionFraction(fullPortionsLeft)}</td>
                  <td className="p-3">
                    {isFull || group.status === 'closed' ? (
                      <button disabled className="px-2 py-1 rounded bg-gray-300 text-white cursor-not-allowed">{group.status === 'closed' ? t('groups.closed') : t('groups.full')}</button>
                    ) : (
                      <button onClick={() => (selectedGroup?._id === group._id ? clearSelection() : handleSelectGroup(group))} className={`px-2 py-1 rounded ${selectedGroup?._id === group._id ? 'bg-gray-500 text-white hover:bg-gray-600' : 'bg-green-500 text-white hover:bg-green-600'}`}>{selectedGroup?._id === group._id ? t('groups.unselect') : t('groups.select')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )) : (
        <p className="text-center text-gray-600 mt-4">{t('groups.noGroups')}</p>
      )}
      {visibleGroups.length > 0 && (
        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      )}

      {selectedGroup && (
        <div ref={selectedPanelRef} className="mt-6 scroll-mt-24">
          <h3 className="text-lg font-bold mb-2 break-words">{t('groups.selectedGroup')} {selectedGroup.name}</h3>
          {(() => {
            const unit = portion || 'Full';
            const mult = getPortionMultiplier(unit);
            return (
              <div className="mb-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="border rounded p-2">
                  <div className="text-gray-600">{t('groups.inGroup', { unit })}</div>
                  <div className="font-semibold text-base">{formatPortionFraction(rawPortionsRemaining(selectedGroup) / mult)}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="text-gray-600">{t('groups.inCart', { unit })}</div>
                  <div className="font-semibold text-base">{formatPortionFraction(cartUnitsFor(selectedGroup._id) / mult)}</div>
                </div>
                <div className="border rounded p-2 border-green-500">
                  <div className="text-gray-600">{t('groups.toAdd', { unit })}</div>
                  <div className="font-semibold text-base">{formatPortionFraction(computePrecisePortionsRemaining(selectedGroup, unit))}</div>
                </div>
              </div>
            );
          })()}

          {noPortionLeft ? (
            <p className="mb-4 text-sm">
              {t('groups.noPortionLeft')}{' '}
              <button type="button" onClick={() => window.dispatchEvent(new Event('open-cart'))} className="text-blue-600 underline">{t('groups.viewCart')}</button>
            </p>
          ) : (
          <>
          <label className="block mb-1 text-sm font-medium">{t('groups.selectPortion')}</label>
          <div className="mb-4">
            <PortionSelector
              group={selectedGroup}
              portion={portion}
              onChange={handlePortionChange}
              isOptionDisabled={(p) => computePrecisePortionsRemaining(selectedGroup) + EPSILON < getPortionMultiplier(p)}
            />
          </div>

          {portion && (
            <fieldset className="mb-4">
              <legend className="block mb-2">{t('groups.howGet')}</legend>
              <label className="mr-6">
                <input
                  type="radio"
                  name="fulfillment"
                  value="pickup"
                  checked={fulfillment === 'pickup'}
                  onChange={() => { setFulfillment('pickup'); setDeliveryQuote(null); }}
                  className="mr-1"
                />
                {t('groups.pickup')}
              </label>
              {deliveryOffered(selectedGroup) && (
              <label>
                <input
                  type="radio"
                  name="fulfillment"
                  value="delivery"
                  checked={fulfillment === 'delivery'}
                  onChange={() => { setFulfillment('delivery'); setDeliveryQuote(null); }}
                  className="mr-1"
                />
                {t('groups.delivery')}
              </label>
              )}
            </fieldset>
          )}

          {portion && fulfillment === 'pickup' && (
            <label className="block text-sm mb-4">
              <span className="block mb-1">{t('groups.pickupPhone')}</span>
              <input
                type="tel"
                value={pickupPhone}
                onChange={(e) => setPickupPhone(e.target.value)}
                className="border p-2 rounded w-full sm:w-1/2"
              />
            </label>
          )}

          {portion && fulfillment === 'delivery' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {ADDRESS_INPUTS.map(([key, label]) => (
                <label key={key} className={`text-sm ${key === 'street' ? 'sm:col-span-2' : ''}`}>
                  <span className="block mb-1">{label}</span>
                  {key === 'state' ? (
                    <select
                      value={address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      className="border p-2 rounded w-full"
                    >
                      {stateOptionsFor(selectedGroup).map((code) => (
                        <option key={code} value={code}>{stateLabel(code)}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={address[key]}
                      onChange={(e) => handleAddressChange(key, e.target.value)}
                      className="border p-2 rounded w-full"
                      autoComplete={key === 'name' ? 'name' : undefined}
                    />
                  )}
                </label>
              ))}
            </div>
          )}

          {portion && (
            <div className="mb-4 border rounded p-3 text-sm space-y-1">
              <p className="font-semibold mb-1">{t('groups.summary')}</p>
              <div className="flex justify-between gap-4"><span>{t('groups.portion')}</span><span>{portion}</span></div>
              <div className="flex justify-between gap-4"><span>{t('groups.fulfillment')}</span><span>{fulfillment === 'delivery' ? t('groups.delivery') : t('groups.pickup')}</span></div>
              <div className="flex justify-between gap-4"><span>{t('groups.price')}</span><span>${calculatedPrice.toFixed(2)}</span></div>
              {fulfillment === 'delivery' && deliveryQuote && (
                <div className="flex justify-between gap-4"><span>Delivery fee (est. {deliveryQuote.estimatedDays} days)</span><span>${deliveryFee.toFixed(2)}</span></div>
              )}
              {cuttingPackagingFee > 0 && (
                <div className="flex justify-between gap-4"><span>{t('groups.cuttingFee')}</span><span>${cuttingPackagingFee.toFixed(2)}</span></div>
              )}
              {(fulfillment !== 'delivery' || deliveryQuote || cuttingPackagingFee > 0) && (
                <div className="flex justify-between gap-4 border-t pt-2 mt-2 text-base font-bold"><span>{t('groups.total')}</span><span>${grandTotal.toFixed(2)}</span></div>
              )}
            </div>
          )}

          <div className="flex gap-4">
              <button onClick={addToCart} disabled={!portion || !selectedGroup || computePrecisePortionsRemaining(selectedGroup) + EPSILON < getPortionMultiplier(portion)} className="bg-blue-600 text-white px-4 py-2 rounded w-full sm:w-auto disabled:opacity-50">
                {t('groups.addToCart')}
              </button>
          </div>
          </>
          )}
        </div>
      )}

      <InfoModal isOpen={!!groupInfo} onClose={() => setGroupInfo(null)} title={t('groupDetails.title')}>
        {groupInfo && (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1 text-sm">
            <section className="space-y-2">
              <SupplierImages images={groupInfo.images} variant="info" galleryTitle={t('groupDetails.galleryTitle')} />
              <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
                <span className="font-semibold">{t('groupDetails.group')}</span><span className="min-w-0 break-words text-base font-bold text-gray-900" title={groupInfo.name}>{groupInfo.name}</span>
                <span className="font-semibold">{t('groupDetails.animal')}</span><span>{animalLabel(t, groupInfo.animal) || t('common.na')}</span>
                <span className="font-semibold">{t('groups.card.supplier')}</span><span className="min-w-0 truncate" title={groupInfo.supplierId?.name || ''}>{groupInfo.supplierId?.displayName || groupInfo.supplierId?.name || t('common.na')}</span>
                <span className="font-semibold">{t('groupDetails.groupStatus')}</span><span className={`justify-self-start whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-white capitalize ${groupInfo.status === 'closed' ? 'bg-gray-500' : 'bg-green-600'}`}>{groupInfo.status || t('common.na')}</span>
              </div>
            </section>
            {groupInfo.shortDescription && (
              <>
                <hr className="border-gray-200" />
                <section>
                  <h3 className="mb-1 font-semibold text-gray-800">{t('groupDetails.aboutTitle')}</h3>
                  <p className="whitespace-pre-line break-words text-gray-700">{groupInfo.shortDescription}</p>
                </section>
              </>
            )}
            <hr className="border-gray-200" />
            <section>
              <h3 className="mb-1 font-semibold text-gray-800">{t('groupDetails.availabilityTitle')}</h3>
              <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
                <span className="font-semibold">{t('groupDetails.membership')}</span><span className={`justify-self-start max-w-full break-words rounded px-2 py-0.5 text-xs font-medium text-white ${membershipOf(groupInfo) === 'Joined' ? 'bg-green-600' : membershipOf(groupInfo) === 'Request Pending' ? 'bg-amber-500' : 'bg-gray-500'}`}>{membershipText(membershipOf(groupInfo))}</span>
                <span className="font-semibold">{t('groupDetails.costPerFull')}</span><span>${deriveCostPerFull(groupInfo).toFixed(2)}</span>
                <span className="font-semibold">{t('groups.card.portionsRemainingFull')}</span><span>{formatPortionFraction(computePrecisePortionsRemaining(groupInfo, 'Full'))}</span>
              </div>
            </section>
          </div>
        )}
      </InfoModal>

      <InfoModal isOpen={modalInfo.isOpen} onClose={closeModal} title={translateKnown(t, modalInfo.title)}>
        {translateKnown(t, modalInfo.content) || <p>{t('groupDetails.noInfo')}</p>}
      </InfoModal>

      <SupplierInfoModal
        isOpen={supplierModalOpen}
        onClose={closeSupplierModal}
        supplier={supplierForModal}
        groupId={supplierModalGroup?._id}
        {...(currentUser && supplierModalGroup && !isJoined(supplierModalGroup) ? {
          onAskToJoin: () => handleRequestJoin(supplierModalGroup),
          joinState: joinRequest.groupId === supplierModalGroup._id ? joinRequest : { status: 'idle', message: '' },
        } : {})}
      />

      {/* Shown when a non-member tries to reserve a share */}
      <InfoModal
        isOpen={!!reserveGroup}
        onClose={() => setReserveGroup(null)}
        title={translateKnown(t, currentUser ? 'Members only' : 'Log in to reserve a share')}
      >
        <p className="text-sm text-gray-700 mb-3">
          {currentUser
            ? t('dialogs.reserve.onlyMembers')
            : t('dialogs.reserve.loginAsMember')}
        </p>
        {currentUser && reserveGroup && (() => {
          const status = joinRequest.groupId === reserveGroup._id ? joinRequest.status : 'idle';
          const message = joinRequest.groupId === reserveGroup._id ? joinRequest.message : '';
          return (
            <div>
              <button
                type="button"
                onClick={() => handleRequestJoin(reserveGroup)}
                disabled={['checking', 'sending', 'sent', 'pending'].includes(status)}
                className="px-3 py-1 rounded bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {['pending', 'sent'].includes(status) ? t('joinStatus.requestPending') : t('joinStatus.requestToJoin')}
              </button>
              {status === 'checking' && <p className="mt-2 text-sm text-gray-600">{t('joinStatus.checking')}</p>}
              {status === 'sending' && <p className="mt-2 text-sm text-gray-600">{t('joinStatus.sending')}</p>}
              {status === 'sent' && <p className="mt-2 text-sm text-green-700">{translateKnown(t, message)}</p>}
              {status === 'pending' && <p className="mt-2 text-sm text-amber-700">{translateKnown(t, message)}</p>}
              {status === 'error' && <p className="mt-2 text-sm text-red-600">{translateKnown(t, message)}</p>}
            </div>
          );
        })()}
      </InfoModal>
    </div>
    </>
  );
}
